/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

// TODO: assign to correct module
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "./SessionProvider.jsx";
import { getMyPrimaryAccount } from "../utils/accountService.js";
import { loadDashboard } from "../utils/dashboardRegistry.js";
import { useLiveModules } from "../hooks/useLiveModules.js";

function firstName(user) {
  const n =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    (user?.email ? user.email.split("@")[0] : "there");
  return n.trim().split(/\s+/)[0];
}

function timeGreeting(d = new Date()) {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

// Brand Command home: the module launcher every user lands on after login.
// `live` here is the build-time default; the real launch switch is
// modules.is_live (Admin → Modules), applied in `resolved` below.
const STROKE = { fill: "none", stroke: "#1B2A4A", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };

const Icons = {
  map: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M9 4 L3 6 V20 L9 18 L15 20 L21 18 V4 L15 6 L9 4 Z" />
      <path d="M9 4 V18 M15 6 V20" />
    </svg>
  ),
  box: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M21 8 L12 3 L3 8 L12 13 L21 8 Z" />
      <path d="M3 8 V16 L12 21 L21 16 V8 M12 13 V21" />
    </svg>
  ),
  inventory: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <rect x="3" y="13" width="5" height="8" rx="1" />
      <rect x="9.5" y="9" width="5" height="12" rx="1" />
      <rect x="16" y="5" width="5" height="16" rx="1" />
    </svg>
  ),
  forecast: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M3 17 L9 11 L13 15 L21 6" />
      <path d="M21 6 H16 M21 6 V11" />
    </svg>
  ),
  people: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20 a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2 a3 3 0 0 1 0 5.6 M17.5 20 a5.5 5.5 0 0 0-3-4.9" />
    </svg>
  ),
  factory: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M3 21 V10 L9 13 V10 L15 13 V8 L21 11 V21 Z" />
      <path d="M7 21 V17 M12 21 V17 M17 21 V17" />
    </svg>
  ),
  posOrder: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M6 2 H18 a1 1 0 0 1 1 1 V21 l-2.2-1.4 -2.3 1.4 -2.5-1.4 -2.5 1.4 -2.3-1.4 L5 21 V3 a1 1 0 0 1 1-1 Z" />
      <path d="M9 7 H15 M9 11 H15 M9 15 H13" />
    </svg>
  ),
  sample: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M9 3 H15 M10 3 V9 L5.5 17 a2 2 0 0 0 1.8 3 H16.7 a2 2 0 0 0 1.8-3 L14 9 V3" />
      <path d="M7.5 14 H16.5" />
    </svg>
  ),
  incentive: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M12 15 a4 4 0 1 0 0-8 4 4 0 0 0 0 8 Z" />
      <path d="M9.5 14 L8 21 L12 19 L16 21 L14.5 14" />
    </svg>
  ),
  spec: (
    <svg viewBox="0 0 24 24" width="28" height="28" {...STROKE}>
      <path d="M8 3 H16 a1 1 0 0 1 1 1 H7 a1 1 0 0 1 1-1 Z" />
      <rect x="4" y="4" width="16" height="17" rx="1.5" />
      <path d="M8 9 H16 M8 13 H16 M8 17 H13" />
    </svg>
  ),
};

const MODULES = {
  "forecasting":      { name: "Sales Forecasting",        to: "/forecasting", live: true,  icon: Icons.forecast,  blurb: "Project demand vs. real sales trends." },
  "distributor-orders": { name: "Distributor Orders",     to: "/distributor-orders", live: true, icon: Icons.box, blurb: "Track distributor orders, forecast vs. ordered, shipped-vs-goal." },
  "incentives":       { name: "Incentive Tracker",        to: "/incentives",  live: true,  icon: Icons.incentive, blurb: "Build, send & score distributor incentives." },
  "depletions":       { name: "Depletions",               to: "/depletions",  live: true,  icon: Icons.inventory, blurb: "Monthly case-sales depletions by SKU." },
  "inventory":        { name: "Inventory",                 to: "/inventory",   live: true,  icon: Icons.box,       blurb: "On-hand stock with months-on-hand." },
  "distribution-map": { name: "Distribution Map",          to: "/distribution-map", live: true,  icon: Icons.map,  blurb: "Embeddable, exportable distributor maps." },
  "pos-orders":       { name: "Point of Sales Orders",      to: "/pos-orders",    live: true,  icon: Icons.posOrder, blurb: "Request POS materials and track shipping." },
  "sample-orders":    { name: "Product Sample Orders",      to: "/sample-orders", live: true,  icon: Icons.sample,    blurb: "Request product samples and track fulfillment." },
  "retail-finder":    { name: "Retail Product Finder",     to: "/retail-finder", live: true,  icon: Icons.map,      blurb: "Where-to-buy store list for your website." },
  "ambassador":       { name: "Brand Ambassador Portal",   live: false,        icon: Icons.people,    blurb: "Manage field teams in one portal." },
  "product-specs":    { name: "Product Specifications",     to: "/product-specs", live: true,  icon: Icons.spec,      blurb: "Full SKU spec sheets with photos & export." },
  "production":       { name: "Production Planning",        live: false,        icon: Icons.factory,   blurb: "Plan co-packer runs to forecast." },
};

// Tiles organized into three business columns.
const COLUMNS = [
  { title: "Sales",      keys: ["forecasting", "incentives", "depletions", "inventory", "distribution-map"] },
  { title: "Marketing",  keys: ["pos-orders", "sample-orders", "retail-finder", "ambassador"] },
  { title: "Operations", keys: ["product-specs", "distributor-orders", "production"] },
];

function Tile({ mod, delay }) {
  const inner = (
    <>
      <div className="module-head">
        <div className="module-icon">{mod.icon}</div>
        <div className="module-name">{mod.name}</div>
        <span className={`module-status ${mod.live ? "live" : "soon"}`}>{mod.live ? "Live" : "Soon"}</span>
      </div>
      <div className="module-blurb">{mod.blurb}</div>
    </>
  );
  const cls = `module-tile enter ${mod.live ? "live" : "soon"}`;
  const style = { animationDelay: `${delay}ms` };
  if (mod.live) {
    return <Link to={mod.to} className={cls} style={style}>{inner}</Link>;
  }
  return <div className={cls} style={style} aria-disabled="true">{inner}</div>;
}

// Remember a per-card toggle choice across reloads (per browser/user).
function useStickyChoice(storageKey, fallback) {
  const [val, setVal] = useState(() => {
    try { return (storageKey && localStorage.getItem(storageKey)) || fallback; } catch { return fallback; }
  });
  const choose = (v) => {
    setVal(v);
    try { if (storageKey) localStorage.setItem(storageKey, v); } catch { /* ignore */ }
  };
  return [val, choose];
}

function KpiCard({ k }) {
  const hasVariants = Array.isArray(k.variants) && k.variants.length > 0;
  const [sel, setSel] = useStickyChoice(
    hasVariants ? k.storageKey : null,
    hasVariants ? (k.defaultVariant || k.variants[0].key) : null,
  );
  const view = hasVariants ? (k.variants.find((v) => v.key === sel) || k.variants[0]) : k;
  const status = view.status || k.status || "neutral";

  const inner = (
    <>
      <div className="dash-kpi-top">
        <div className="dash-kpi-label">{k.label}</div>
        {hasVariants ? (
          <div className="dash-kpi-toggle">
            {k.variants.map((v) => (
              <button
                key={v.key} type="button"
                className={v.key === view.key ? "on" : ""}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSel(v.key); }}
              >
                {v.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="dash-kpi-value">
        {view.display}{view.unit ? <span className="dash-kpi-unit"> {view.unit}</span> : null}
      </div>
      {view.sub ? <div className="dash-kpi-sub">{view.sub}</div> : null}
    </>
  );
  const cls = `dash-kpi status-${status}`;
  return k.to ? <Link to={k.to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

function SignalRow({ s }) {
  const inner = (
    <>
      <span className="dash-signal-dot" aria-hidden="true" />
      <span className="dash-signal-body">
        <span className="dash-signal-title">{s.title}</span>
        {s.detail ? <span className="dash-signal-detail">{s.detail}</span> : null}
      </span>
      {s.actionLabel ? <span className="dash-signal-action">{s.actionLabel} →</span> : null}
    </>
  );
  const cls = `dash-signal sev-${s.severity || "info"}`;
  return s.to ? <Link to={s.to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>;
}

export default function Home() {
  const { user } = useSession();
  const [account, setAccount] = useState(null);
  const [dash, setDash] = useState({ kpis: [], signals: [] });
  const [dashLoading, setDashLoading] = useState(true);
  const allKeys = Object.keys(MODULES);

  // `live` in the MODULES map above is now only a build-time default. The real
  // launch switch is `modules.is_live`, flipped by staff in Admin → Modules,
  // so a finished module can be held back (or released) without a deploy.
  //
  // Joined on ROUTE rather than on the map's key: the keys here and the
  // module_ids in the catalog were coined at different times and don't all
  // match ("incentives" vs "incentive-tracker"), whereas the route is the same
  // string in both places by definition. A module with no route yet can't be
  // live anyway, so it correctly stays a Soon tile.
  const { isLiveRoute } = useLiveModules();

  const resolved = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(MODULES)) {
      // Only ever NARROWS: a module the catalog holds back becomes a Soon tile.
      // Nothing here can promote a tile the code hasn't built a route for.
      out[k] = { ...v, live: v.live && isLiveRoute(v.to) };
    }
    return out;
  }, [isLiveRoute]);

  const liveCount = allKeys.filter((k) => resolved[k].live).length;

  useEffect(() => {
    let alive = true;
    getMyPrimaryAccount().then((a) => { if (alive) setAccount(a); }).catch(() => {});
    loadDashboard()
      .then((d) => { if (alive) setDash(d); })
      .catch(() => {})
      .finally(() => { if (alive) setDashLoading(false); });
    return () => { alive = false; };
  }, []);

  const { kpis, signals } = dash;
  const needs = signals.length;
  const summary = dashLoading
    ? "Loading your operations hub…"
    : kpis.length === 0 && needs === 0
      ? "Your hub is ready — add depletions, inventory & programs to light up KPIs and alerts."
      : `Your operations hub is live.${needs ? ` ${needs} item${needs === 1 ? "" : "s"} need${needs === 1 ? "s" : ""} you.` : " You're all caught up."}`;

  return (
    <section>
      <div className="home-head enter">
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {account?.logoUrl ? (
            <img
              src={account.logoUrl}
              alt={account.name || "Company logo"}
              style={{ maxHeight: 72, maxWidth: 260, width: "auto", height: "auto", objectFit: "contain", display: "block", borderRadius: 10, flex: "0 0 auto" }}
            />
          ) : (
            <span style={{ width: 72, height: 72, borderRadius: 16, display: "grid", placeItems: "center", background: "var(--primary-soft, #e8edf5)", color: "var(--primary, #1B2A4A)", fontWeight: 800, fontSize: 30, flex: "0 0 auto" }}>
              {(account?.name || "?").slice(0, 1).toUpperCase()}
            </span>
          )}
          <div>
            {account?.name ? <p className="dash-eyebrow">{account.name}</p> : null}
            <h1 style={{ margin: 0 }}>{timeGreeting()}, {firstName(user)}</h1>
            <p className="sub" style={{ margin: "4px 0 0" }}>{summary}</p>
          </div>
        </div>
      </div>

      {/* KPI strip — whatever the modules contribute (grows as modules add data) */}
      {dashLoading ? (
        <div className="dash-kpis enter" style={{ animationDelay: "30ms" }}>
          {[0, 1, 2].map((i) => <div key={i} className="dash-kpi dash-kpi-skel" />)}
        </div>
      ) : kpis.length > 0 ? (
        <div className="dash-kpis enter" style={{ animationDelay: "30ms" }}>
          {kpis.map((k) => <KpiCard key={k.key} k={k} />)}
        </div>
      ) : null}

      {/* Needs you — cross-module action signals */}
      {(dashLoading || needs > 0) && (
        <div className="dash-needs enter" style={{ animationDelay: "50ms" }}>
          <div className="dash-needs-head">
            <h2>Needs you</h2>
            {needs > 0 ? <span className="dash-needs-count">{needs}</span> : null}
          </div>
          {dashLoading ? (
            <p className="muted" style={{ margin: 0 }}>Checking your data…</p>
          ) : (
            signals.slice(0, 6).map((s) => <SignalRow key={s.key} s={s} />)
          )}
        </div>
      )}

      <div className="section-row enter" style={{ animationDelay: "70ms" }}>
        <h2>Brand Operating Modules</h2>
        <span className="caption">{liveCount} live · {allKeys.length - liveCount} coming soon</span>
      </div>
      <div className="module-columns">
        {COLUMNS.map((col, ci) => (
          <div className="module-col enter" key={col.title} style={{ animationDelay: `${90 + ci * 50}ms` }}>
            <div className="module-col-head">{col.title}</div>
            {col.keys.map((k, i) => (
              <Tile key={k} mod={resolved[k]} delay={110 + (ci * 3 + i) * 40} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
